import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import { useNavigate, useParams } from 'react-router-dom'
import {
  adminService,
  type EventWritePayload,
  type TargetingRule,
} from '../../services/adminService'
import AssetUploadButton from '../AssetUploadButton'
import EventBrandShell from '../../components/EventBrandShell'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { assetIdFromUrl } from '../assetUrl'

/** ISO <-> input[type=datetime-local] helpers. */
function toLocalInput(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const off = d.getTimezoneOffset()
  const local = new Date(d.getTime() - off * 60000)
  return local.toISOString().slice(0, 16)
}
function fromLocalInput(local: string): string {
  return local ? new Date(local).toISOString() : ''
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

interface FormState {
  name: string
  slug: string
  tagline: string
  startsAt: string
  endsAt: string
  timezone: string
  location: string
  venueName: string
  venueAddress: string
  venueCity: string
  venueMapUrl: string
  welcomeMessage: string
  dressCode: string
  travel: string
  primaryColor: string
  accentColor: string
  logoUrl: string
  heroImage: string
  loginBackgroundUrl: string
  emailFromName: string
  emailFromAddress: string
  faqs: { question: string; answer: string }[]
  contacts: { name: string; role: string; email: string; phone: string }[]
  travelVariants: { label: string; body: string; targetingRuleId: string }[]
  registrationMode: string // invite_only/public
  registrationOpen: boolean
  registrationCapacity: string // free text; '' = unlimited
  registrationClosesAt: string // datetime-local; '' = no close
  registrationCode: string // '' = no code required
}

const EMPTY: FormState = {
  name: '',
  slug: '',
  tagline: '',
  startsAt: '',
  endsAt: '',
  timezone: 'America/Chicago',
  location: '',
  venueName: '',
  venueAddress: '',
  venueCity: '',
  venueMapUrl: '',
  welcomeMessage: '',
  dressCode: '',
  travel: '',
  primaryColor: '',
  accentColor: '',
  logoUrl: '',
  heroImage: '',
  loginBackgroundUrl: '',
  emailFromName: '',
  emailFromAddress: '',
  faqs: [],
  contacts: [],
  travelVariants: [],
  registrationMode: 'invite_only',
  registrationOpen: false,
  registrationCapacity: '',
  registrationClosesAt: '',
  registrationCode: '',
}

export default function AdminEventFormPage() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const { notify } = useToast()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [slugTouched, setSlugTouched] = useState(isEdit)

  useEffect(() => {
    if (!isEdit || !id) return
    adminService
      .getEvent(id)
      .then((e) => {
        setForm({
          name: e.name,
          slug: e.slug ?? '',
          tagline: e.tagline ?? '',
          startsAt: toLocalInput(e.startsAt),
          endsAt: toLocalInput(e.endsAt),
          timezone: e.timezone,
          location: e.location ?? '',
          venueName: e.venue?.name ?? '',
          venueAddress: e.venue?.address ?? '',
          venueCity: e.venue?.city ?? '',
          venueMapUrl: e.venue?.mapUrl ?? '',
          welcomeMessage: e.welcomeMessage ?? '',
          dressCode: e.dressCode ?? '',
          travel: e.travel ?? '',
          primaryColor: e.primaryColor ?? '',
          accentColor: e.accentColor ?? '',
          logoUrl: e.logoUrl ?? '',
          heroImage: e.heroImage ?? '',
          loginBackgroundUrl: e.loginBackgroundUrl ?? '',
          emailFromName: e.emailFromName ?? '',
          emailFromAddress: e.emailFromAddress ?? '',
          faqs: (e.faqs ?? []).map((f) => ({
            question: f.question,
            answer: f.answer,
          })),
          contacts: (e.contacts ?? []).map((c) => ({
            name: c.name,
            role: c.role ?? '',
            email: c.email,
            phone: c.phone ?? '',
          })),
          travelVariants: (e.travelVariants ?? []).map((v) => ({
            label: v.label ?? '',
            body: v.body,
            targetingRuleId: v.targetingRuleId ?? '',
          })),
          registrationMode: e.registrationMode ?? 'invite_only',
          registrationOpen: e.registrationOpen ?? false,
          registrationCapacity:
            e.registrationCapacity != null ? String(e.registrationCapacity) : '',
          registrationClosesAt: toLocalInput(e.registrationClosesAt ?? undefined),
          registrationCode: e.registrationCode ?? '',
        })
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
  }, [id, isEdit, notify])

  // Targeting rules for the travel-variant "which group" dropdown.
  const [rules, setRules] = useState<TargetingRule[]>([])
  useEffect(() => {
    if (!isEdit || !id) return
    adminService
      .listRules(id)
      .then(setRules)
      .catch(() => setRules([]))
  }, [id, isEdit])

  const set = (key: keyof FormState) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }))

  // FAQ list editors (add / edit one field / remove one row).
  const addFaq = () =>
    setForm((f) => ({ ...f, faqs: [...f.faqs, { question: '', answer: '' }] }))
  const setFaq = (i: number, key: 'question' | 'answer', value: string) =>
    setForm((f) => ({
      ...f,
      faqs: f.faqs.map((q, idx) => (idx === i ? { ...q, [key]: value } : q)),
    }))
  const removeFaq = (i: number) =>
    setForm((f) => ({ ...f, faqs: f.faqs.filter((_, idx) => idx !== i) }))

  // Key-contact list editors (add / edit one field / remove one row).
  const addContact = () =>
    setForm((f) => ({
      ...f,
      contacts: [...f.contacts, { name: '', role: '', email: '', phone: '' }],
    }))
  const setContact = (
    i: number,
    key: 'name' | 'role' | 'email' | 'phone',
    value: string,
  ) =>
    setForm((f) => ({
      ...f,
      contacts: f.contacts.map((c, idx) =>
        idx === i ? { ...c, [key]: value } : c,
      ),
    }))
  const removeContact = (i: number) =>
    setForm((f) => ({
      ...f,
      contacts: f.contacts.filter((_, idx) => idx !== i),
    }))

  // Travel-variant editors (per-group personalized travel).
  const addVariant = () =>
    setForm((f) => ({
      ...f,
      travelVariants: [
        ...f.travelVariants,
        { label: '', body: '', targetingRuleId: '' },
      ],
    }))
  const setVariant = (
    i: number,
    key: 'label' | 'body' | 'targetingRuleId',
    value: string,
  ) =>
    setForm((f) => ({
      ...f,
      travelVariants: f.travelVariants.map((v, idx) =>
        idx === i ? { ...v, [key]: value } : v,
      ),
    }))
  const removeVariant = (i: number) =>
    setForm((f) => ({
      ...f,
      travelVariants: f.travelVariants.filter((_, idx) => idx !== i),
    }))

  const [removing, setRemoving] = useState<
    'logoUrl' | 'heroImage' | 'loginBackgroundUrl' | null
  >(null)

  /**
   * Remove a logo/hero image: delete the stored object (when it's one of ours),
   * clear the field, and — on an existing event — persist the clear immediately
   * so the attendee site falls back to the default brand without waiting for a
   * full Save. (The event PATCH is exclude_unset, so we send an explicit null.)
   */
  const removeAsset = (
    key: 'logoUrl' | 'heroImage' | 'loginBackgroundUrl',
  ) => async () => {
    const url = form[key].trim()
    if (!url) return
    const label =
      key === 'logoUrl'
        ? 'logo'
        : key === 'heroImage'
          ? 'hero image'
          : 'login background'
    if (!window.confirm(`Remove this ${label}? This deletes the uploaded file.`))
      return
    setRemoving(key)
    try {
      const assetId = assetIdFromUrl(url)
      if (assetId && isEdit && id) {
        // Best-effort object delete; ignore 404 (already gone / external URL).
        await adminService.deleteAsset(id, assetId).catch(() => undefined)
      }
      setForm((f) => ({ ...f, [key]: '' }))
      if (isEdit && id) {
        await adminService.updateEvent(id, { [key]: null })
      }
      notify(`Removed ${label}`)
    } catch (err) {
      notify(apiError(err), 'error')
    } finally {
      setRemoving(null)
    }
  }

  const onName = (value: string) =>
    setForm((f) => ({
      ...f,
      name: value,
      slug: slugTouched ? f.slug : slugify(value),
    }))

  const handleSubmit = async () => {
    if (!form.name || !form.slug || !form.startsAt || !form.endsAt) {
      notify('Name, slug, start and end are required', 'error')
      return
    }
    // A partially-filled contact (has one of name/email but not both) would be
    // silently dropped — flag it instead so the admin isn't surprised.
    const partialContact = form.contacts.some((c) => {
      const name = c.name.trim()
      const email = c.email.trim()
      return (name || email) && !(name && email)
    })
    if (partialContact) {
      notify('Each contact needs both a name and an email', 'error')
      return
    }
    const payload: EventWritePayload = {
      name: form.name,
      slug: form.slug,
      tagline: form.tagline || undefined,
      startsAt: fromLocalInput(form.startsAt),
      endsAt: fromLocalInput(form.endsAt),
      timezone: form.timezone || undefined,
      location: form.location || undefined,
      venueName: form.venueName || undefined,
      venueAddress: form.venueAddress || undefined,
      venueCity: form.venueCity || undefined,
      venueMapUrl: form.venueMapUrl || undefined,
      welcomeMessage: form.welcomeMessage || undefined,
      dressCode: form.dressCode || undefined,
      travel: form.travel || undefined,
      // Send explicit values (empty -> null) so branding can be cleared on edit.
      primaryColor: form.primaryColor.trim() || undefined,
      accentColor: form.accentColor.trim() || undefined,
      logoUrl: form.logoUrl.trim() || undefined,
      heroImage: form.heroImage.trim() || undefined,
      loginBackgroundUrl: form.loginBackgroundUrl.trim() || undefined,
      // Per-event email sender override. Empty -> null clears it (revert to the
      // platform-wide default sender).
      emailFromName: form.emailFromName.trim() || null,
      emailFromAddress: form.emailFromAddress.trim() || null,
      // Always send the FAQ list (backend replaces it wholesale), dropping any
      // blank rows. An empty array clears all FAQs.
      faqs: form.faqs
        .map((f) => ({ question: f.question.trim(), answer: f.answer.trim() }))
        .filter((f) => f.question || f.answer),
      // Same for key contacts. Drop rows missing the required name/email;
      // role/phone are optional. An empty array clears all contacts.
      contacts: form.contacts
        .map((c) => ({
          name: c.name.trim(),
          role: c.role.trim() || undefined,
          email: c.email.trim(),
          phone: c.phone.trim() || undefined,
        }))
        .filter((c) => c.name && c.email),
      // Personalized travel per group. Backend replaces wholesale; drop rows
      // without content. (A row without a rule is inert until one is chosen.)
      travelVariants: form.travelVariants
        .map((v) => ({
          label: v.label.trim() || undefined,
          body: v.body.trim(),
          targetingRuleId: v.targetingRuleId || undefined,
        }))
        .filter((v) => v.body),
      // Public self-registration settings.
      registrationMode: form.registrationMode,
      registrationOpen: form.registrationOpen,
      registrationCapacity: form.registrationCapacity.trim()
        ? Number(form.registrationCapacity)
        : null,
      registrationClosesAt: form.registrationClosesAt
        ? fromLocalInput(form.registrationClosesAt)
        : null,
      registrationCode: form.registrationCode.trim() || null,
    }
    setSaving(true)
    try {
      if (isEdit && id) {
        await adminService.updateEvent(id, payload)
        notify('Event saved')
        navigate(`/admin/events/${id}`)
      } else {
        const created = await adminService.createEvent(payload)
        notify('Event created')
        navigate(`/admin/events/${created.id}`)
      }
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(isEdit && id ? `/admin/events/${id}` : '/admin')}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back
      </Button>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        {isEdit ? 'Edit event' : 'New event'}
      </Typography>

      <Stack spacing={3}>
        <Section title="Basics">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Event name"
                fullWidth
                required
                value={form.name}
                onChange={(e) => onName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Slug"
                fullWidth
                required
                helperText="URL identifier (unique)"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true)
                  set('slug')(e.target.value)
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Timezone"
                fullWidth
                value={form.timezone}
                onChange={(e) => set('timezone')(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tagline"
                fullWidth
                value={form.tagline}
                onChange={(e) => set('tagline')(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Starts"
                type="datetime-local"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={form.startsAt}
                onChange={(e) => set('startsAt')(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ends"
                type="datetime-local"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={form.endsAt}
                onChange={(e) => set('endsAt')(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Location (short)"
                fullWidth
                placeholder="The Adolphus, Dallas, TX"
                value={form.location}
                onChange={(e) => set('location')(e.target.value)}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Venue">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Venue name"
                fullWidth
                value={form.venueName}
                onChange={(e) => set('venueName')(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City / State"
                fullWidth
                value={form.venueCity}
                onChange={(e) => set('venueCity')(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                value={form.venueAddress}
                onChange={(e) => set('venueAddress')(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Venue map / address"
                fullWidth
                value={form.venueMapUrl}
                onChange={(e) => set('venueMapUrl')(e.target.value)}
                placeholder="e.g. The Adolphus, 1321 Commerce St, Dallas, TX — or a Google Maps link"
                helperText="Paste the venue address, its name, or any Google Maps link — an interactive map is shown automatically. (Advanced: a Maps 'Embed a map' URL also works.)"
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Welcome & logistics">
          <Stack spacing={2}>
            <TextField
              label="Welcome message"
              fullWidth
              multiline
              minRows={3}
              value={form.welcomeMessage}
              onChange={(e) => set('welcomeMessage')(e.target.value)}
            />
            <TextField
              label="Dress code"
              fullWidth
              value={form.dressCode}
              onChange={(e) => set('dressCode')(e.target.value)}
            />
            <TextField
              label="Travel & parking (default)"
              fullWidth
              multiline
              minRows={2}
              value={form.travel}
              onChange={(e) => set('travel')(e.target.value)}
              helperText="Shown to everyone who doesn't match a personalized travel variant below."
            />
          </Stack>
        </Section>

        <Section title="Public registration">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Let anyone with the link register themselves (a HubSpot-style landing
            page) instead of only inviting a pre-set guest list. Registrants
            confirm their email, an admin approves them, and then they receive a
            link to access the event and mobile app. Uses the fields configured
            on the <strong>Registration</strong> tab.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Registration mode"
              select
              fullWidth
              value={form.registrationMode}
              onChange={(e) => set('registrationMode')(e.target.value)}
            >
              <MenuItem value="invite_only">Invite only (guest list)</MenuItem>
              <MenuItem value="public">Public self-registration</MenuItem>
            </TextField>
            {form.registrationMode === 'public' && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.registrationOpen}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          registrationOpen: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Registration is open"
                />
                {form.registrationOpen && isEdit && form.slug && (
                  <Typography variant="body2" color="text.secondary">
                    Public link:{' '}
                    <strong>{`${window.location.origin}/e/${form.slug}/register`}</strong>
                  </Typography>
                )}
                <TextField
                  label="Capacity (approved registrations)"
                  type="number"
                  fullWidth
                  value={form.registrationCapacity}
                  onChange={(e) => set('registrationCapacity')(e.target.value)}
                  helperText="Leave blank for unlimited. Only approved registrants count toward capacity."
                />
                <TextField
                  label="Registration closes at"
                  type="datetime-local"
                  fullWidth
                  value={form.registrationClosesAt}
                  onChange={(e) => set('registrationClosesAt')(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank for no close date."
                />
                <TextField
                  label="Invitation code"
                  fullWidth
                  value={form.registrationCode}
                  onChange={(e) => set('registrationCode')(e.target.value)}
                  helperText="Registrants must enter this exact code to register. A correct code also skips the manual approval step (they're auto-approved after confirming their email). Leave blank to require no code."
                />
              </>
            )}
          </Stack>
        </Section>

        <Section title="Personalized travel by group">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Show different travel info to different client groups. Each variant
            targets a group (a targeting rule — e.g. a “Group” tag) and overrides
            the default travel above for attendees who match. The first matching
            variant wins; everyone else sees the default.
            {!isEdit && ' Save the event first, then add variants.'}
          </Typography>
          {isEdit && rules.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No targeting rules yet — create one on the <strong>Targeting</strong>{' '}
              tab (e.g. Tag: Group is any of Acme), then it will appear here.
            </Typography>
          )}
          {isEdit && (
            <Stack spacing={2}>
              {form.travelVariants.map((v, i) => (
                <Card key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField
                        label="Label (optional)"
                        size="small"
                        sx={{ width: 200 }}
                        value={v.label}
                        onChange={(e) => setVariant(i, 'label', e.target.value)}
                        placeholder="e.g. Acme"
                      />
                      <TextField
                        select
                        label="Group (targeting rule)"
                        size="small"
                        fullWidth
                        value={v.targetingRuleId}
                        onChange={(e) =>
                          setVariant(i, 'targetingRuleId', e.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>— select a group —</em>
                        </MenuItem>
                        {rules.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeVariant(i)}
                      >
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <TextField
                      label="Travel info for this group"
                      fullWidth
                      multiline
                      minRows={2}
                      value={v.body}
                      onChange={(e) => setVariant(i, 'body', e.target.value)}
                    />
                  </Stack>
                </Card>
              ))}
              <Box>
                <Button startIcon={<AddRoundedIcon />} onClick={addVariant}>
                  Add travel variant
                </Button>
              </Box>
            </Stack>
          )}
        </Section>

        <Section title="FAQs">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Questions and answers shown to attendees on the event. Leave empty for
            no FAQ section.
          </Typography>
          <Stack spacing={2}>
            {form.faqs.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No FAQs yet.
              </Typography>
            )}
            {form.faqs.map((faq, i) => (
              <Box
                key={i}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    <TextField
                      label={`Question ${i + 1}`}
                      fullWidth
                      value={faq.question}
                      onChange={(e) => setFaq(i, 'question', e.target.value)}
                    />
                    <TextField
                      label="Answer"
                      fullWidth
                      multiline
                      minRows={2}
                      value={faq.answer}
                      onChange={(e) => setFaq(i, 'answer', e.target.value)}
                    />
                  </Stack>
                  <IconButton
                    aria-label="Remove FAQ"
                    color="error"
                    onClick={() => removeFaq(i)}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Stack>
              </Box>
            ))}
            <Box>
              <Button startIcon={<AddRoundedIcon />} onClick={addFaq}>
                Add FAQ
              </Button>
            </Box>
          </Stack>
        </Section>

        <Section title="Key contacts">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            People attendees can reach out to (event manager, relationship
            director, etc.). Name and email are required for each.
          </Typography>
          <Stack spacing={2}>
            {form.contacts.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No contacts yet.
              </Typography>
            )}
            {form.contacts.map((contact, i) => (
              <Box
                key={i}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Grid container spacing={1.5} sx={{ flex: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Name"
                        fullWidth
                        value={contact.name}
                        onChange={(e) => setContact(i, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Role"
                        fullWidth
                        placeholder="Event Manager"
                        value={contact.role}
                        onChange={(e) => setContact(i, 'role', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email"
                        type="email"
                        fullWidth
                        value={contact.email}
                        onChange={(e) => setContact(i, 'email', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Phone"
                        fullWidth
                        placeholder="+1 (214) 555-0142"
                        value={contact.phone}
                        onChange={(e) => setContact(i, 'phone', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <IconButton
                    aria-label="Remove contact"
                    color="error"
                    onClick={() => removeContact(i)}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Stack>
              </Box>
            ))}
            <Box>
              <Button startIcon={<AddRoundedIcon />} onClick={addContact}>
                Add contact
              </Button>
            </Box>
          </Stack>
        </Section>

        <Section title="Branding">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize this event's look. Leave blank to use the default Vericast
            brand.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ColorField
                label="Primary color"
                value={form.primaryColor}
                onChange={set('primaryColor')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ColorField
                label="Accent color"
                value={form.accentColor}
                onChange={set('accentColor')}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Logo URL"
                  fullWidth
                  placeholder="https://…/logo.svg"
                  value={form.logoUrl}
                  onChange={(e) => set('logoUrl')(e.target.value)}
                />
                <AssetUploadButton
                  eventId={id}
                  onUploaded={(url) => set('logoUrl')(url)}
                />
                {form.logoUrl.trim() && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={removeAsset('logoUrl')}
                    disabled={removing !== null}
                  >
                    {removing === 'logoUrl' ? 'Removing…' : 'Remove'}
                  </Button>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Hero image URL"
                  fullWidth
                  placeholder="https://…/hero.png"
                  value={form.heroImage}
                  onChange={(e) => set('heroImage')(e.target.value)}
                />
                <AssetUploadButton
                  eventId={id}
                  onUploaded={(url) => set('heroImage')(url)}
                />
                {form.heroImage.trim() && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={removeAsset('heroImage')}
                    disabled={removing !== null}
                  >
                    {removing === 'heroImage' ? 'Removing…' : 'Remove'}
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>

          <BrandingPreview
            name={form.name || 'Your event'}
            tagline={form.tagline}
            primary={form.primaryColor}
            accent={form.accentColor}
            logoUrl={form.logoUrl}
            heroImage={form.heroImage}
          />
        </Section>

        <Section title="Login page">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each event has its own branded sign-in page using the colors and logo
            above. Add a login-specific background image below (falls back to the
            hero image, then the default gradient). Share this event's sign-in
            link so attendees land on the branded page.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Login background image URL"
                  fullWidth
                  placeholder="https://…/login-bg.jpg"
                  value={form.loginBackgroundUrl}
                  onChange={(e) => set('loginBackgroundUrl')(e.target.value)}
                />
                <AssetUploadButton
                  eventId={id}
                  onUploaded={(url) => set('loginBackgroundUrl')(url)}
                />
                {form.loginBackgroundUrl.trim() && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={removeAsset('loginBackgroundUrl')}
                    disabled={removing !== null}
                  >
                    {removing === 'loginBackgroundUrl' ? 'Removing…' : 'Remove'}
                  </Button>
                )}
              </Stack>
            </Grid>
            {isEdit && form.slug && (
              <Grid item xs={12}>
                <Button
                  variant="text"
                  component="a"
                  href={`/e/${form.slug}/signin`}
                  target="_blank"
                  rel="noopener"
                  startIcon={<LaunchRoundedIcon />}
                >
                  View login page
                </Button>
              </Grid>
            )}
          </Grid>

          <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Live preview
          </Typography>
          <Box
            sx={{
              mt: 1,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <EventBrandShell
              compact
              brand={{
                name: form.name || 'Your event',
                tagline: form.tagline,
                primaryColor: form.primaryColor,
                accentColor: form.accentColor,
                logoUrl: form.logoUrl,
                loginBackgroundUrl: form.loginBackgroundUrl,
                heroImage: form.heroImage,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {form.name || 'Your event'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Enter the email on your invitation and we'll send you a secure
                sign-in link.
              </Typography>
              <TextField label="Email address" size="small" fullWidth disabled sx={{ mb: 1 }} />
              <Button variant="contained" fullWidth disabled>
                Email me a link
              </Button>
            </EventBrandShell>
          </Box>
        </Section>

        <Section title="Email sender">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Override the platform-wide email sender for this event. Emails sent for
            this event (invites, sign-in links, confirmations, custom emails) will
            come from this name and address. Leave blank to use the global default
            set in Admin settings.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                label="From name"
                fullWidth
                placeholder="e.g. Acme Summit Team"
                value={form.emailFromName}
                onChange={(e) => set('emailFromName')(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                label="From address"
                fullWidth
                type="email"
                placeholder="e.g. summit@acme.com"
                value={form.emailFromAddress}
                onChange={(e) => set('emailFromAddress')(e.target.value)}
              />
            </Grid>
          </Grid>
        </Section>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button color="inherit" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button variant="contained" size="large" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create event'}
          </Button>
        </Box>
      </Stack>
    </Container>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      {children}
    </Card>
  )
}

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

/** A hex text field with a native color swatch/picker. */
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  const valid = value === '' || HEX_RE.test(value)
  const swatch = HEX_RE.test(value) ? value : '#ffffff'
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box
        component="input"
        type="color"
        aria-label={`${label} picker`}
        value={swatch}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value.toUpperCase())
        }
        sx={{
          mt: 0.5,
          width: 44,
          height: 44,
          p: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: 'transparent',
          cursor: 'pointer',
        }}
      />
      <TextField
        label={label}
        fullWidth
        placeholder="#0075BC"
        value={value}
        error={!valid}
        helperText={!valid ? 'Enter a hex color like #0075BC' : ' '}
        onChange={(e) => onChange(e.target.value)}
      />
    </Stack>
  )
}

/** Live preview of the event hero with the chosen branding. */
function BrandingPreview({
  name,
  tagline,
  primary,
  accent,
  logoUrl,
  heroImage,
}: {
  name: string
  tagline: string
  primary: string
  accent: string
  logoUrl: string
  heroImage: string
}) {
  const p = HEX_RE.test(primary) ? primary : '#0075BC'
  const a = HEX_RE.test(accent) ? accent : '#5BC6CC'
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        PREVIEW
      </Typography>
      <Box
        sx={{
          mt: 1,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          color: '#fff',
          minHeight: 150,
          background: heroImage
            ? `linear-gradient(135deg, ${p}E6, ${p}99), url(${heroImage}) center/cover`
            : `linear-gradient(135deg, ${p} 0%, ${p} 55%, ${a} 140%)`,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {logoUrl && (
          <Box
            component="img"
            src={logoUrl}
            alt="Event logo"
            sx={{ height: 28, mb: 1.5, alignSelf: 'flex-start', filter: 'brightness(0) invert(1)' }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        )}
        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.15 }}>
          {name}
        </Typography>
        {tagline && (
          <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
            {tagline}
          </Typography>
        )}
        <Box
          sx={{
            mt: 2,
            alignSelf: 'flex-start',
            px: 2,
            py: 0.75,
            borderRadius: 9999,
            bgcolor: '#fff',
            color: p,
            fontWeight: 600,
            fontSize: '0.8rem',
          }}
        >
          View my agenda
        </Box>
      </Box>
    </Box>
  )
}
